import React, { useState, useCallback } from "react";
import { Box, Typography, Fade, Button, Stack, Chip, alpha } from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { useCalendar } from "./hooks/useCalendar";
import { useCalendarFilters } from "./hooks/useCalendarFilters";
import { useServiceEditor } from "./hooks/useServiceEditor";
import { useCalendarData } from "./hooks/useCalendarData";
import { CalendarHeader } from "./components/CalendarHeader";
import { CalendarGrid } from "./components/CalendarGrid";
import { ServiceList } from "./components/ServiceList";
import { ServiceDetailsPanel } from "./components/ServiceDetailsPanel";
import { ServiceEditForm } from "./components/ServiceEditForm";
import { CreatePestReportModal } from "./components/CreatePestReportModal";
import { ViewPestReportModal } from "./components/ViewPestReportModal";
import { ProgressBars } from "./components/ProgressBars";
import ModalConfirmUniversal from "../../components/ModalConfirmUniversal";
import servicesService from "../../api/servicesService";
import Procedias from "./components/Procedias";
import AddEventDialog from "./components/AddEventDialog";
import { isInternalCalendarEvent, isGhostOccurrence, eventBelongsToCalendarDay } from "./utils";
import { getBusinessDateParts } from "../../utils/dateUtils";
 
/**
 * Componente principal del Calendario
 */
export default function Calendario() {
  const [selectedDay, setSelectedDay] = useState(() => getBusinessDateParts().day);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [pendingDeleteService, setPendingDeleteService] = useState(null);
  const [openProcedias, setOpenProcedias] = useState(false);
  const [openAddEventDialog, setOpenAddEventDialog] = useState(false);
  const [completeLoading, setCompleteLoading] = useState(false);
  const [pestReportModalOpen, setPestReportModalOpen] = useState(false);
  const [viewPestReportModalOpen, setViewPestReportModalOpen] = useState(false);
 
  // Hooks personalizados
  const {
    currentDate,
    events,
    loading: calendarLoading,
    changeMonth,
    refreshEvents,
  } = useCalendar();

  const handleMonthChange = useCallback(
    (direction) => {
      const next = new Date(currentDate);
      next.setDate(1);
      next.setMonth(currentDate.getMonth() + direction);
      changeMonth(direction);
      const biz = getBusinessDateParts();
      if (next.getFullYear() === biz.year && next.getMonth() === biz.month) {
        setSelectedDay(biz.day);
      } else {
        setSelectedDay(null);
      }
    },
    [changeMonth, currentDate]
  );
 
  const {
    filterType,
    filterValue,
    filterOptions,
    filteredEvents,
    setFilterType,
    setFilterValue,
    clearFilters,
  } = useCalendarFilters(events);
 
  const {
    selectedService,
    editing,
    editForm,
    loading: editorLoading,
    openEditor,
    closeEditor,
    updateFormField,
    saveService,
    selectService,
    setSelectedService,
  } = useServiceEditor(currentDate, refreshEvents);
 
  const { tecnicos, loadBranchesByClient } = useCalendarData();
 
  // Eventos del día seleccionado (usando filteredEvents para que respete el filtro activo)
  const selectedDayEvents = [
    ...new Map(
      filteredEvents
        .filter((e) => eventBelongsToCalendarDay(e, selectedDay))
        .map((e) => [
          `${e.originalServiceId}-${e.technician?._id}`,
          e,
        ])
    ).values(),
  ];
 
  // Eventos que se muestran en el grid del calendario
  const isFiltered = Boolean(filterType && filterValue);
  const calendarEvents = isFiltered ? filteredEvents : events;
 
  // Handlers
  const handleDaySelect = useCallback(
    (day) => {
      setSelectedDay(day);
      setSelectedService(null);
      closeEditor();
    },
    [setSelectedService, closeEditor]
  );
 
  const handleServiceSelect = useCallback(
    (service) => {
      if (selectedService?._id === service._id) {
        setSelectedService(null);
        closeEditor();
        return;
      }
      selectService(service);
      const clientId = service.client?._id || service.client;
      if (clientId) {
        loadBranchesByClient(clientId);
      }
    },
    [selectedService, selectService, loadBranchesByClient, setSelectedService, closeEditor]
  );
 
  const handleEdit = useCallback(() => {
    if (isGhostOccurrence(selectedService)) return;
    if (selectedService) {
      openEditor(selectedService);
      const clientId = selectedService.client?._id || selectedService.client;
      if (clientId) {
        loadBranchesByClient(clientId);
      }
    }
  }, [selectedService, openEditor, loadBranchesByClient]);
 
  const handleCreatePestReport = useCallback(() => {
    if (isGhostOccurrence(selectedService)) return;
    setPestReportModalOpen(true);
  }, [selectedService]);
 
  const handleViewPestReport = useCallback(() => {
    setViewPestReportModalOpen(true);
  }, []);
 
  const handlePestReportSuccess = useCallback(async () => {
    setPestReportModalOpen(false);
    await refreshEvents();
    setSelectedService(null);
  }, [setSelectedService, refreshEvents]);
 
  const handleSave = useCallback(async () => {
    try {
      await saveService();
    } catch (err) {
      alert("Error guardando. Revisa la consola para más detalles.");
    }
  }, [saveService]);
 
  const handleDeleteClick = useCallback(() => {
    if (isGhostOccurrence(selectedService)) return;
    setPendingDeleteService(selectedService);
    setConfirmDeleteOpen(true);
  }, [selectedService]);
 
  const handleDeleteConfirm = useCallback(async () => {
    const svc = pendingDeleteService;
    setConfirmDeleteOpen(false);
    setPendingDeleteService(null);
 
    if (!svc) return;
 
    try {
      await servicesService.delete(svc._id || svc.id);
      await refreshEvents();
      setSelectedService(null);
      closeEditor();
    } catch (err) {
      console.error("Error eliminando servicio:", err);
      alert("No se pudo eliminar el servicio. Revisa la consola.");
    }
  }, [pendingDeleteService, refreshEvents, setSelectedService, closeEditor]);
 
  const handleFilterTypeChange = useCallback(
    (type) => {
      setFilterType(type);
      setFilterValue("");
      setSelectedService(null);
      closeEditor();
    },
    [setFilterType, setFilterValue, setSelectedService, closeEditor]
  );
 
  return (
    <Box
      sx={{
        height: "calc(100vh - 115px)",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.default",
        overflow: "hidden",
      }}
    >
      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          gap: 1,
          p: 1,
          overflow: "hidden",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* LEFT: Calendar Section */}
        <Box
          sx={{
            flex: isFiltered ? "1.7 1 0%" : "1.25 1 0%",
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            overflow: "hidden",
            position: "relative",
            zIndex: 1,
          }}
        >
          <CalendarHeader
            currentDate={currentDate}
            onMonthChange={handleMonthChange}
            filterType={filterType}
            filterValue={filterValue}
            filterOptions={filterOptions}
            onFilterTypeChange={handleFilterTypeChange}
            onFilterValueChange={setFilterValue}
            onClear={clearFilters}
            events={filteredEvents}
          />
 
          {/* Barras de Progreso */}
          <Box
            sx={{
              position: "relative",
              zIndex: 10,
              overflow: "visible",
            }}
          >
            {/*
              ProgressBars recibe:
              - events: TODOS los eventos del mes (sin filtro) → para la barra de "Servicios completados"
              - filteredEvents: eventos filtrados → para la barra de "Reportes subidos" cuando hay filtro activo
              - filterType / filterValue: para saber si hay filtro y qué etiqueta mostrar
            */}
            <ProgressBars
              events={events}
              filteredEvents={filteredEvents}
              currentDate={currentDate}
              filterType={filterType}
              filterValue={filterValue}
            />
          </Box>
 
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              position: "relative",
              zIndex: 1,
            }}
          >
            <CalendarGrid
              currentDate={currentDate}
              events={calendarEvents}
              selectedDay={selectedDay}
              onDaySelect={handleDaySelect}
              showBranches={isFiltered}
            />
          </Box>
        </Box>
 
        {/* RIGHT: Details & Events Section */}
        <Box
          sx={{
            flex: isFiltered ? "0.9 1 0%" : "1 1 0%",
            minWidth: isFiltered ? 320 : 360,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            overflow: "hidden",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              px: 1.5,
              py: 1.15,
              bgcolor: "white",
              borderRadius: "16px",
              border: "1px solid",
              borderColor: alpha("#0f172a", 0.06),
              boxShadow: "0 8px 24px rgba(15,23,42,0.05)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              flexShrink: 0,
            }}
          >
            <Box minWidth={0}>
              <Typography
                noWrap
                sx={{
                  fontWeight: 800,
                  fontSize: "0.88rem",
                  color: selectedDay ? "#0f172a" : alpha("#0f172a", 0.45),
                  letterSpacing: "-0.02em",
                  lineHeight: 1.2,
                  textTransform: "capitalize",
                }}
              >
                {selectedDay
                  ? new Date(
                      currentDate.getFullYear(),
                      currentDate.getMonth(),
                      selectedDay
                    ).toLocaleDateString("es-MX", {
                      weekday: "long",
                      day: "numeric",
                      month: "short",
                    })
                  : "Sin día seleccionado"}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.68rem",
                  color: alpha("#0f172a", 0.5),
                  mt: 0.15,
                }}
              >
                {selectedDay
                  ? `${selectedDayEvents.length} en agenda`
                  : "Toca un día del calendario"}
              </Typography>
            </Box>

            <Stack direction="row" spacing={0.75} flexShrink={0}>
              <Button
                size="small"
                variant="contained"
                disableElevation
                startIcon={<AssignmentIcon sx={{ fontSize: 16 }} />}
                disabled={!selectedDay || selectedDayEvents.length === 0}
                onClick={() => setOpenProcedias(true)}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  borderRadius: "10px",
                  px: 1.25,
                  py: 0.55,
                  minWidth: 0,
                }}
              >
                Procedias
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
                disabled={!selectedDay}
                onClick={() => setOpenAddEventDialog(true)}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  borderRadius: "10px",
                  px: 1.1,
                  py: 0.5,
                  minWidth: 0,
                }}
              >
                Evento
              </Button>
            </Stack>
          </Box>

          {selectedService && (
            <Fade in={Boolean(selectedService)} timeout={220}>
              <Box
                sx={{
                  bgcolor: "white",
                  borderRadius: "16px",
                  border: "1px solid",
                  borderColor: alpha("#0f172a", 0.06),
                  boxShadow:
                    "0 1px 2px rgba(15,23,42,0.05), 0 10px 24px rgba(15,23,42,0.08)",
                  overflow: "auto",
                  flex: "0 1 auto",
                  maxHeight: editing ? "calc(100% - 96px)" : "none",
                  minHeight: 0,
                  "&::-webkit-scrollbar": { width: "6px" },
                  "&::-webkit-scrollbar-thumb": {
                    bgcolor: "rgba(15,23,42,0.18)",
                    borderRadius: "8px",
                  },
                }}
              >
                {editing ? (
                  <ServiceEditForm
                    formData={editForm}
                    technicians={tecnicos}
                    onFieldChange={updateFormField}
                    onSave={handleSave}
                    onCancel={closeEditor}
                    loading={editorLoading}
                    service={selectedService}
                  />
                ) : (
                  <ServiceDetailsPanel
                    service={selectedService}
                    onEdit={handleEdit}
                    onDelete={handleDeleteClick}
                    onClose={() => {
                      setSelectedService(null);
                      closeEditor();
                    }}
                    onCreatePestReport={handleCreatePestReport}
                    onViewPestReport={handleViewPestReport}
                    loading={editorLoading}
                  />
                )}
              </Box>
            </Fade>
          )}

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              bgcolor: "white",
              borderRadius: "16px",
              border: "1px solid",
              borderColor: alpha("#0f172a", 0.06),
              boxShadow: "0 8px 24px rgba(15,23,42,0.05)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                px: 1.5,
                py: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                borderBottom: "1px solid",
                borderColor: alpha("#0f172a", 0.06),
              }}
            >
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: "0.82rem",
                  color: "#0f172a",
                  letterSpacing: "-0.02em",
                }}
              >
                Agenda del día
              </Typography>
              {selectedDay ? (
                <Chip
                  label={selectedDayEvents.length}
                  size="small"
                  sx={{
                    height: 20,
                    fontWeight: 800,
                    fontSize: "0.65rem",
                    bgcolor: alpha("#2563eb", 0.1),
                    color: "#1d4ed8",
                    "& .MuiChip-label": { px: 0.8 },
                  }}
                />
              ) : null}
            </Box>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: "auto",
                p: 1.15,
                bgcolor: alpha("#0f172a", 0.015),
                "&::-webkit-scrollbar": { width: "6px" },
                "&::-webkit-scrollbar-thumb": {
                  bgcolor: "rgba(15,23,42,0.16)",
                  borderRadius: "8px",
                },
              }}
            >
              <ServiceList
                events={selectedDayEvents}
                selectedService={selectedService}
                onSelectService={handleServiceSelect}
                loading={calendarLoading}
                filterType={filterType}
                selectedDay={selectedDay}
              />
            </Box>
          </Box>
        </Box>
      </Box>
 
      {/* Delete Confirmation Modal */}
      <ModalConfirmUniversal
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        title={
          isInternalCalendarEvent(pendingDeleteService)
            ? "Eliminar evento"
            : "Eliminar servicio"
        }
        message={
          isInternalCalendarEvent(pendingDeleteService)
            ? "¿Eliminar este evento? Dejará de aparecer en las procedias de los técnicos asignados."
            : isGhostOccurrence(pendingDeleteService)
            ? "¿Quitar esta ocurrencia proyectada? No se volverá a copiar automáticamente en este mes."
            : "¿Estás seguro de que deseas eliminar este servicio? Esta acción no se puede deshacer."
        }
        confirmText="Eliminar"
        cancelText="Cancelar"
        color="error"
        onConfirm={handleDeleteConfirm}
      />
 
      {openProcedias && (
        <Procedias
          open={openProcedias}
          onClose={() => setOpenProcedias(false)}
          selectedDay={selectedDay}
          date={currentDate.toLocaleDateString("es-MX", {
            month: "long",
            year: "numeric",
          })}
          services={selectedDayEvents}
        />
      )}
 
      <AddEventDialog
        open={openAddEventDialog}
        onClose={() => setOpenAddEventDialog(false)}
        onSave={refreshEvents}
        selectedDate={selectedDay}
        currentDate={currentDate}
        technicians={tecnicos}
      />
 
      <CreatePestReportModal
        open={pestReportModalOpen}
        service={selectedService}
        onClose={() => setPestReportModalOpen(false)}
        onSuccess={handlePestReportSuccess}
        loading={completeLoading}
      />
 
      <ViewPestReportModal
        open={viewPestReportModalOpen}
        service={selectedService}
        onClose={() => setViewPestReportModalOpen(false)}
      />
    </Box>
  );
}
 